import * as React from "react";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Typography from "@mui/material/Typography";
import { Box, Chip, IconButton } from "@mui/material";
import MaleIcon from "@mui/icons-material/Male";
import FemaleIcon from "@mui/icons-material/Female";
import StoreIcon from "@mui/icons-material/Store";
import FavoriteBorderIcon from "@mui/icons-material/FavoriteBorder";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

const ListCard = ({ listData, classes, view }) => {
  const plans = ["Gold", "Diamond Special", "Diamond", "Platinum"];
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const wishlistItems = useSelector((state) => state.Wishlist?.items || []);

  const formatDate = (dateString) => new Date(dateString).getUTCFullYear();

  const getCategoryName = (id) => {
    if (!classes?.length) return "";
    const cat = classes.find((c) => c.id === id);
    return cat?.name || "";
  };

  const isInWishlist = (id) => wishlistItems.some((item) => item.id === id);

  const toggleWishlist = (e, item) => {
    e.stopPropagation();
    if (isInWishlist(item.id)) {
      dispatch({ type: "REMOVE_FROM_WISHLIST", payload: item.id });
    } else {
      dispatch({
        type: "ADD_TO_WISHLIST",
        payload: {
          id: item.id,
          name: item.name,
          price: item.price,
          media: item.media,
          category_id: item.category_id,
          sex: item.sex,
          created_at: item.created_at,
          status: item.status,
          seller: item.seller,
          on_hold: item.on_hold,
        },
      });
    }
  };

  return (
    <>
      {listData.map((item) => (
        <Box key={item?.id} onClick={() => navigate("/listing/" + item?.id)}>
          <Card
            sx={{
              backgroundColor: "#1a1a1a",
              border: "1px solid #2a2a2a",
              borderRadius: "14px",
              cursor: "pointer",
              overflow: "hidden",
              transition: "all 0.3s ease",
              "&:hover": {
                borderColor: "#FFC001",
                transform: "translateY(-3px)",
                boxShadow: "0 8px 25px rgba(255, 192, 1, 0.12)",
                "& .listing-img": { transform: "scale(1.05)" },
              },
            }}
          >
            {/* Image area */}
            <Box sx={{ position: "relative", overflow: "hidden" }}>
              <CardMedia
                className="listing-img"
                sx={{
                  height: 190,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  transition: "transform 0.4s ease",
                }}
                image={item?.media?.[0]?.url}
                title={item?.name}
              />

              {/* Gradient overlay */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0, left: 0, right: 0,
                  height: "50px",
                  background: "linear-gradient(to top, rgba(26,26,26,1) 0%, transparent 100%)",
                  pointerEvents: "none",
                }}
              />

              {/* Price badge */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 8, right: 8,
                  backgroundColor: "#FFC001",
                  color: "#000",
                  fontWeight: 700,
                  fontSize: "14px",
                  px: 1.2,
                  py: 0.3,
                  borderRadius: "7px",
                }}
              >
                ${item.price}
              </Box>

              {/* Sex icon */}
              <Box
                sx={{
                  position: "absolute",
                  top: 8, left: 8,
                  backgroundColor: "rgba(0,0,0,0.55)",
                  borderRadius: "50%",
                  width: 28, height: 28,
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}
              >
                {item.sex === "male" ? (
                  <MaleIcon sx={{ color: "#4da6ff", fontSize: 16 }} />
                ) : item.sex === "female" ? (
                  <FemaleIcon sx={{ color: "#ff69b4", fontSize: 16 }} />
                ) : (
                  <Box sx={{ display: "flex" }}>
                    <MaleIcon sx={{ color: "#4da6ff", fontSize: 13 }} />
                    <FemaleIcon sx={{ color: "#ff69b4", fontSize: 13 }} />
                  </Box>
                )}
              </Box>

              {/* Wishlist heart */}
              <IconButton
                onClick={(e) => toggleWishlist(e, item)}
                sx={{
                  position: "absolute",
                  top: 6, right: 6,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.75)" },
                  padding: "5px",
                  zIndex: 2,
                }}
              >
                {isInWishlist(item.id) ? (
                  <FavoriteIcon sx={{ color: "#FFC001", fontSize: 18 }} />
                ) : (
                  <FavoriteBorderIcon sx={{ color: "#fff", fontSize: 18 }} />
                )}
              </IconButton>

              {/* Status badges */}
              {item?.status === "sold" && (
                <Chip label="SOLD" size="small" sx={{ position: "absolute", top: 8, left: 44, backgroundColor: "#EF0107", color: "#fff", fontWeight: 700, fontSize: "10px", height: "20px" }} />
              )}
              {item?.on_hold && (
                <Chip label="ON HOLD" size="small" sx={{ position: "absolute", top: 8, left: 44, backgroundColor: "#FFD300", color: "#000", fontWeight: 700, fontSize: "10px", height: "20px" }} />
              )}
              {item?.status === "nfs" && (
                <Chip label="NFS" size="small" sx={{ position: "absolute", top: 8, left: 44, backgroundColor: "#333", color: "#fff", fontWeight: 700, fontSize: "10px", height: "20px" }} />
              )}
            </Box>

            {/* Card content */}
            <Box sx={{ px: 1.5, pt: 1, pb: 1.2 }}>
              <Typography noWrap sx={{ color: "#fff", fontWeight: 700, fontSize: "14px", lineHeight: 1.3 }}>
                {item.name}
              </Typography>
              <Typography noWrap sx={{ color: "#666", fontSize: "11px", mt: 0.3 }}>
                {getCategoryName(item.category_id)}
              </Typography>

              {/* Store name */}
              {plans.includes(item?.seller?.user?.subscriptions?.[0]?.plan?.name) && (
                <Box
                  onClick={(e) => { e.stopPropagation(); navigate("/neighbour/" + item?.seller?.store?.id); }}
                  sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5, "&:hover": { "& *": { color: "#FFC001" } } }}
                >
                  <StoreIcon sx={{ color: "#555", fontSize: 13 }} />
                  <Typography noWrap sx={{ color: "#555", fontSize: "11px" }}>
                    {item?.seller?.store?.brand_name}
                  </Typography>
                </Box>
              )}

              {/* Year */}
              <Typography sx={{ color: "#444", fontSize: "11px", mt: 0.5 }}>
                {formatDate(item.created_at)}
              </Typography>
            </Box>
          </Card>
        </Box>
      ))}
    </>
  );
};

export default ListCard;
